import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { FormattedMessage } from '@flipster/i18n';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { TradingLeaderboardEvent } from '@flipster/utils/segment/events/trading-leaderboard';

import { SegmentPage } from '../../../../misc/segment/segment-page';
import { entryPopupItemBunja } from '../../bunja';
import { leagueLeaderboardOnboardingBunja } from './bunja';
import LeaderboardOnboardingImage from './img_leaderboard_onboarding.webp';

interface LeagueLeaderboardOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const LeagueLeaderboardOnboardingModal = ({
  isOpen,
  onClose,
  onExit,
}: LeagueLeaderboardOnboardingModalProps) => {
  const t = useTranslations();
  const { markAsRead } = useBunja(entryPopupItemBunja);
  const { descriptionsAtom } = useBunja(leagueLeaderboardOnboardingBunja);
  const descriptions = useAtomValue(descriptionsAtom);

  function handleClose() {
    markAsRead('TIERED_LEAGUE_DESCRIPTION');
    onClose();
  }

  return (
    <>
      <SegmentPage name={TradingLeaderboardEvent.LeagueOnboardingView} context={{ event_location: 'leaderboard' }} />
      <Modal isOpen={isOpen} onExit={onExit}>
        <Modal.Close onClose={handleClose} />
        <Modal.Header css={{ textStyle: 'Title/M', color: 'content.default.level-5' }}>
          <FormattedMessage id='Autogen_common.r251126.s_You_ve_joined_the_league' />
        </Modal.Header>
        <Modal.Content css={stack.raw({ gap: { base: 24, tablet: 16 } })}>
          <img
            src={LeaderboardOnboardingImage.src}
            alt='Tap to view leagues'
            className={css({
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'border.default.level-2',
              rounded: 8,
            })}
          />
          <div className={stack({ textStyle: 'Text/L/Regular', color: 'content.default.level-3', gap: 16 })}>
            <p>
              <FormattedMessage id='Autogen_common.r251126.s_You_re_placed_into_weekly_and_monthly_leagues_based_on_your_asset_balance_Keep_trading_to_climb_higher' />
            </p>
            <div className={stack({ gap: 2 })}>
              <p className={css({ textStyle: 'Text/L/Strong' })}>
                <FormattedMessage id='Autogen_common.r251126.s_League_tiers' />
              </p>
              {descriptions && (
                <ul className={css({ ml: 24, '&, & > li': { listStyle: 'disc' } })}>
                  {descriptions.map(([label, description], index) => (
                    <li key={index}>
                      {label}: {description}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Button
            size='large'
            label={t('Autogen_common.r251126.s_View_my_rank')}
            onClick={() => {
              segment.track(TradingLeaderboardEvent.LeagueOnboardingClick, {
                event_location: 'leaderboard',
              });
              handleClose();
            }}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};
